import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignedDocumentActions } from '../../../../hooks/useSignedDocumentActions';
import { useDocuments } from './useDocuments';
import { useDeleteDocument } from './useDeleteDocument';
import { useDocumentPdf } from './useDocumentPdf';
import { useSignDocument } from './useSignDocument';
import { useSignedDocumentFile } from './useSignedDocumentFile';

function toMessage(value, fallback) {
  if (typeof value === 'string' && value.trim()) return value;
  return fallback;
}

// Orchestration of the patient "Documentos" tab, shared by the web and mobile lists.
export function useDocumentsScreen(patientId) {
  const navigate = useNavigate();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const notifySuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessMessage(true);
  };
  const notifyError = (message) => {
    setErrorMessage(message);
    setShowErrorMessage(true);
  };

  const { documents, isLoading, error: listError, refetch } = useDocuments(patientId);

  useEffect(() => {
    if (listError) {
      setErrorMessage(toMessage(listError, 'Erro ao carregar os documentos.'));
      setShowErrorMessage(true);
    }
  }, [listError]);

  // Editor
  const [isEditorOpen, setEditorOpen] = useState(false);
  const [editingDocumentId, setEditingDocumentId] = useState(null);

  const openNewDocument = () => {
    setEditingDocumentId(null);
    setEditorOpen(true);
  };
  const openDocument = (documentId) => {
    setEditingDocumentId(documentId);
    setEditorOpen(true);
  };
  const closeEditor = () => {
    setEditorOpen(false);
    setEditingDocumentId(null);
  };
  const onDocumentSaved = (message) => {
    notifySuccess(message);
    closeEditor();
    refetch();
  };

  // Delete
  const { remove, isDeleting } = useDeleteDocument();
  const [deleteModal, setDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);

  const handleDeleteClick = (document) => {
    setDocumentToDelete(document);
    setDeleteModal(true);
  };
  const closeDeleteModal = () => {
    setDeleteModal(false);
    setDocumentToDelete(null);
  };
  const handleConfirmDelete = () => {
    remove(patientId, documentToDelete.id)
      .then((returnFromApi) => {
        notifySuccess(toMessage(returnFromApi.data, 'Documento excluído com sucesso.'));
        closeDeleteModal();
        refetch();
      })
      .catch((returnFromApi) => {
        notifyError(toMessage(returnFromApi.response?.data, 'Erro ao excluir o documento.'));
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  };

  // Print: PDF rendered by the backend, the same one used for the digital signature.
  const { fetchPdf, isLoading: isLoadingPdf } = useDocumentPdf();

  const printDocument = (documentId) => {
    // Opens the tab synchronously so the popup blocker allows it.
    const documentWindow = window.open('', '_blank');
    fetchPdf(patientId, documentId)
      .then((returnFromApi) => {
        const url = window.URL.createObjectURL(
          new Blob([returnFromApi.data], { type: 'application/pdf' }),
        );
        if (documentWindow) {
          documentWindow.location.href = url;
        } else {
          window.location.assign(url);
        }
        // Gives the new tab time to load the blob before releasing it.
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      })
      .catch((returnFromApi) => {
        documentWindow?.close();
        notifyError('Erro ao gerar o PDF do documento.');
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  };

  // Digital signature
  const { sign } = useSignDocument();
  const { fetchUrl: fetchSignedDocumentUrl } = useSignedDocumentFile();
  const {
    documentToSign,
    openSignModal,
    closeSignModal,
    onSigned,
    openSignedDocument,
    isOpeningSignedDocument,
  } = useSignedDocumentActions({
    fetchSignedDocumentUrl: ({ documentId, signatureId }) =>
      fetchSignedDocumentUrl(patientId, documentId, signatureId),
    refetch,
    onSuccess: (message) => notifySuccess(toMessage(message, 'Documento assinado com sucesso.')),
    onError: notifyError,
  });

  const signDocument = (certificatePassword) =>
    sign(patientId, documentToSign.id, { certificatePassword });

  // The next signature becomes a new version; the earlier ones stay available.
  const signSummary = documentToSign
    ? `${documentToSign.title} · versão ${(documentToSign.signedVersions?.length ?? 0) + 1}`
    : null;

  const openSignedVersion = (document, version) =>
    openSignedDocument({ documentId: document.id, signatureId: version.id });

  return {
    documents,
    isBusy: isLoading || isDeleting || isLoadingPdf || isOpeningSignedDocument,
    messages: {
      showSuccessMessage,
      setShowSuccessMessage,
      successMessage,
      showErrorMessage,
      setShowErrorMessage,
      errorMessage,
    },
    editor: {
      isEditorOpen,
      editingDocumentId,
      openNewDocument,
      openDocument,
      closeEditor,
      onDocumentSaved,
    },
    deletion: {
      deleteModal,
      documentToDelete,
      handleDeleteClick,
      closeDeleteModal,
      handleConfirmDelete,
    },
    printDocument,
    signature: {
      documentToSign,
      openSignModal,
      closeSignModal,
      onSigned,
      openSignedVersion,
      signDocument,
      signSummary,
    },
  };
}
